import React, { useEffect, useState } from "react";
import {
  Eye,
  MousePointerClick,
  ShoppingBag,
  Zap,
  Tag,
  Building2,
  CheckCircle2,
  Layers,
} from "lucide-react";
import api from "@/lib/api";

export const AnalyticsTab = () => {
  const [mode, setMode] = useState("active"); // 'active' | 'rollup'
  const [data, setData] = useState(null);
  const [rollup, setRollup] = useState(null);

  useEffect(() => {
    if (mode === "active") {
      api.get("/dispensary/analytics").then((r) => setData(r.data));
    } else {
      api.get("/dispensary/analytics/rollup").then((r) => setRollup(r.data));
    }
  }, [mode]);

  return (
    <div>
      <ModeSwitch mode={mode} setMode={setMode} rollupShops={rollup?.shops?.length} />
      {mode === "rollup" ? (
        <RollupView data={rollup} />
      ) : (
        <ActiveView data={data} />
      )}
    </div>
  );
};

const ModeSwitch = ({ mode, setMode, rollupShops }) => (
  <div className="flex items-center gap-1 bg-card border border-border rounded-full p-1 w-fit mb-6" data-testid="analytics-mode-switch">
    <ModeBtn active={mode === "active"} onClick={() => setMode("active")} testid="analytics-mode-active">
      <Building2 className="w-3 h-3" /> This shop
    </ModeBtn>
    <ModeBtn active={mode === "rollup"} onClick={() => setMode("rollup")} testid="analytics-mode-rollup">
      <Layers className="w-3 h-3" /> All shops
      {rollupShops > 1 && (
        <span className="ml-1 text-[10px] text-foreground/60">{rollupShops}</span>
      )}
    </ModeBtn>
  </div>
);

const ModeBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 h-8 rounded-full text-xs flex items-center gap-1.5 transition-colors ${
      active ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary/60"
    }`}
  >
    {children}
  </button>
);

// --------------------------------------------------------------------------
// Single-shop view (existing behavior)
// --------------------------------------------------------------------------
const ActiveView = ({ data }) => {
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { products, totals } = data;
  const boosted = products.filter((p) => p.boosted);
  const nonBoosted = products.filter((p) => !p.boosted);
  const avgCtr = (arr) => {
    const tot = arr.reduce((s, p) => s + p.impressions, 0);
    const ord = arr.reduce((s, p) => s + p.order_clicks, 0);
    return tot ? ((ord / tot) * 100).toFixed(2) : "0.00";
  };
  const boostLift =
    boosted.length && nonBoosted.length
      ? (parseFloat(avgCtr(boosted)) / Math.max(0.01, parseFloat(avgCtr(nonBoosted)))).toFixed(1)
      : null;

  return (
    <>
      <div className="grid sm:grid-cols-4 gap-5 mb-6">
        <Stat icon={<Eye className="w-4 h-4" />} title="Impressions" value={totals.impressions.toLocaleString()}>
          Across all products
        </Stat>
        <Stat icon={<MousePointerClick className="w-4 h-4" />} title="Card clicks" value={totals.card_clicks.toLocaleString()}>
          Shoppers exploring
        </Stat>
        <Stat icon={<ShoppingBag className="w-4 h-4" />} title="Order Now clicks" value={totals.order_clicks.toLocaleString()}>
          Sent to your checkout
        </Stat>
        <Stat icon={<Zap className="w-4 h-4" />} title="Boost lift" value={boostLift ? `${boostLift}x` : "—"}>
          {boostLift ? "Boosted vs unboosted CTR" : "Buy a Boost to compare"}
        </Stat>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Impressions</th>
              <th className="px-4 py-3 font-medium text-right">Card clicks</th>
              <th className="px-4 py-3 font-medium text-right">Order clicks</th>
              <th className="px-4 py-3 font-medium text-right">CTR</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr
                key={p.product_id}
                className="border-t border-border"
                data-testid={`analytics-row-${p.product_id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-serif text-base">{p.strain_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.brand} · {p.category} · {p.unit}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {p.boosted && (
                      <span
                        className="pill text-[10px]"
                        style={{
                          color: "#D4A268",
                          borderColor: "rgba(212,162,104,0.5)",
                          background: "rgba(212,162,104,0.12)",
                        }}
                      >
                        <Zap className="w-3 h-3" /> Boost
                      </span>
                    )}
                    {p.deal_active && (
                      <span className="pill bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px]">
                        <Tag className="w-3 h-3" /> Deal
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">{p.impressions.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{p.card_clicks.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-foreground font-medium">
                  {p.order_clicks.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right">{p.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {products.length > 0 && totals.impressions === 0 && (
        <p className="mt-4 text-xs text-muted-foreground">
          No data yet. Numbers populate as customers browse your products.
        </p>
      )}
    </>
  );
};

// --------------------------------------------------------------------------
// Multi-shop rollup
// --------------------------------------------------------------------------
const RollupView = ({ data }) => {
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { shops, grand } = data;
  if (!shops.length)
    return (
      <div className="border border-dashed border-border rounded-2xl py-16 text-center text-muted-foreground">
        You don't own any shops yet.
      </div>
    );

  return (
    <>
      <div className="grid sm:grid-cols-4 gap-5 mb-6">
        <Stat icon={<Building2 className="w-4 h-4" />} title="Locations" value={shops.length}>
          {shops.filter((s) => s.is_active).length} active in dashboard
        </Stat>
        <Stat icon={<Eye className="w-4 h-4" />} title="Impressions" value={grand.impressions.toLocaleString()}>
          All locations combined
        </Stat>
        <Stat icon={<ShoppingBag className="w-4 h-4" />} title="Order clicks" value={grand.order_clicks.toLocaleString()}>
          {grand.ctr}% CTR overall
        </Stat>
        <Stat icon={<Zap className="w-4 h-4" />} title="Live products" value={grand.products.toLocaleString()}>
          Across all shops
        </Stat>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-x-auto" data-testid="analytics-rollup-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Location</th>
              <th className="px-4 py-3 font-medium text-right">Products</th>
              <th className="px-4 py-3 font-medium text-right">Boosted</th>
              <th className="px-4 py-3 font-medium text-right">Deals</th>
              <th className="px-4 py-3 font-medium text-right">Impressions</th>
              <th className="px-4 py-3 font-medium text-right">Order clicks</th>
              <th className="px-4 py-3 font-medium text-right">CTR</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <tr
                key={s.slug}
                className="border-t border-border"
                data-testid={`analytics-rollup-row-${s.slug}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-base">{s.name}</div>
                    {s.is_active && (
                      <span className="pill text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{s.city}</div>
                </td>
                <td className="px-4 py-3 text-right">{s.products_count}</td>
                <td className="px-4 py-3 text-right">{s.boosted_count}</td>
                <td className="px-4 py-3 text-right">{s.deal_count}</td>
                <td className="px-4 py-3 text-right">{s.impressions.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-foreground font-medium">
                  {s.order_clicks.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right">{s.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {grand.impressions === 0 && (
        <p className="mt-4 text-xs text-muted-foreground">
          No traffic yet. Once shoppers browse any of your locations, numbers populate here.
        </p>
      )}
    </>
  );
};

const Stat = ({ icon, title, value, children }) => (
  <div className="border border-border rounded-2xl bg-card p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
      <span className="text-primary">{icon}</span>
      {title}
    </div>
    <div className="font-serif text-3xl mt-2">{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{children}</div>
  </div>
);
